import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { COPY_BARCODE_PATTERN, generateCopyBarcode, sanitizeCopyBarcode } from '@/lib/barcode';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Minus, QrCode } from 'lucide-react';
import { QRScanner } from '@/components/ui/QRScanner';
import {
  useCreateBook,
  useAuthors,
  usePublishers,
  useGenres,
  type CreateBookData
} from '@/hooks/catalog-hooks';
import {
  useCreateBookCopy,
  useShelfLocations,
  useConditions,
  type CreateBookCopyData
} from '@/hooks/inventory-hooks';
import Drawer from '@/components/admin/components/Drawer';

// Schema for creating a book together with its copies
const createBookWithInventorySchema = z.object({
  // Catalog information
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional(),
  isbn: z.string().min(10, 'Valid ISBN is required'),
  publication_date: z.string().min(1, 'Publication date is required'),
  description: z.string().optional(),
  language: z.string().min(1, 'Language is required'),
  pages: z.number().min(1).optional(),
  edition: z.string().optional(),
  series: z.string().optional(),
  series_number: z.number().min(1).optional(),
  author_ids: z.array(z.number()).min(1, 'At least one author is required'),
  genre_ids: z.array(z.number()).optional(),
  publisher_id: z.number().min(1, 'Publisher is required'),
  cover_image: z.instanceof(File).optional(),

  // Inventory information
  create_copies: z.boolean().default(true),
  copies: z.array(z.object({
    barcode: z.string().regex(COPY_BARCODE_PATTERN, 'Barcode must be exactly 12 digits'),
    accession_number: z.string().min(5, 'Accession number must be at least 5 characters'),
    acquisition_date: z.string().min(1, 'Acquisition date is required'),
    acquisition_cost: z.string().optional(),
    acquisition_source: z.string().optional(),
    location_id: z.number().optional(),
    condition_id: z.number().min(1, 'Condition is required'),
    status: z.enum(['available', 'maintenance', 'withdrawn']).default('available'),
    notes: z.string().optional(),
  })).min(1, 'At least one copy is required').optional(),
});

type CreateBookWithInventoryFormData = z.infer<typeof createBookWithInventorySchema>;

const buildDefaultFormValues = (): CreateBookWithInventoryFormData => ({
  title: '',
  subtitle: '',
  isbn: '',
  publication_date: '',
  description: '',
  language: 'English',
  pages: undefined,
  edition: '',
  series: '',
  series_number: undefined,
  author_ids: [],
  genre_ids: [],
  publisher_id: 0,
  cover_image: undefined,
  create_copies: true,
  copies: [{
    barcode: generateCopyBarcode(),
    accession_number: '',
    acquisition_date: new Date().toISOString().split('T')[0],
    acquisition_cost: '',
    acquisition_source: '',
    location_id: undefined,
    condition_id: 1,
    status: 'available',
    notes: '',
  }],
});

interface AddBookWithInventoryFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const inputCls = "w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-lg text-sm text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-[var(--lib-gold)]/15 bg-transparent";
const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-1.5";

export const AddBookWithInventoryForm: React.FC<AddBookWithInventoryFormProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('catalog');
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);

  // API hooks
  const createBookMutation = useCreateBook();
  const createBookCopyMutation = useCreateBookCopy();

  // Reference data
  const { data: authorsData } = useAuthors({ page_size: 100 });
  const { data: publishersData } = usePublishers({ page_size: 100 });
  const { data: genresData } = useGenres({ page_size: 100 });
  const { data: locationsData } = useShelfLocations({ page_size: 100 });
  const { data: conditionsData } = useConditions({ page_size: 100 });

  const authors = authorsData?.results || [];
  const publishers = publishersData?.results || [];
  const genres = genresData?.results || [];
  const locations = locationsData?.results || [];
  const conditions = conditionsData?.results || [];

  const form = useForm<CreateBookWithInventoryFormData>({
    resolver: zodResolver(createBookWithInventorySchema),
    defaultValues: buildDefaultFormValues(),
  });

  const { watch, setValue, getValues } = form;
  const createCopies = watch('create_copies');
  const copies = watch('copies') || [];

  useEffect(() => {
    if (open) {
      form.reset(buildDefaultFormValues());
    }
  }, [form, open]);

  // Generate accession number helper
  const generateAccessionNumber = (index: number) => {
    const year = new Date().getFullYear();
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const copyNumber = (index + 1).toString().padStart(3, '0');
    return `${year}.${month}.${copyNumber}`;
  };

  // Add new copy
  const addCopy = () => {
    const currentCopies = getValues('copies') || [];
    const newIndex = currentCopies.length;
    const newCopy = {
      barcode: generateCopyBarcode(),
      accession_number: generateAccessionNumber(newIndex),
      acquisition_date: new Date().toISOString().split('T')[0],
      acquisition_cost: '',
      acquisition_source: '',
      location_id: undefined,
      condition_id: 1,
      status: 'available' as const,
      notes: '',
    };
    setValue('copies', [...currentCopies, newCopy]);
  };

  // Remove copy
  const removeCopy = (index: number) => {
    const currentCopies = getValues('copies') || [];
    if (currentCopies.length > 1) {
      const newCopies = currentCopies.filter((_, i) => i !== index);
      setValue('copies', newCopies);
    }
  };

  // Auto-generate barcodes and accession numbers when needed
  const autoFillCopyData = (index: number) => {
    const currentCopies = getValues('copies') || [];
    const copy = currentCopies[index];

    if (!copy.barcode) {
      setValue(`copies.${index}.barcode`, generateCopyBarcode());
    }
    if (!copy.accession_number) {
      setValue(`copies.${index}.accession_number`, generateAccessionNumber(index));
    }
  };

  // Handle QR code scan
  const handleQRScan = (qrData: string) => {
    try {
      // Try to parse as JSON first (could be API-fetched book data or raw QR data)
      const bookData = JSON.parse(qrData);

      // If QRScanner attached a flag for API-sourced data, prefer that path
      const fromAPI = !!bookData.isFromAPI || !!(bookData.title || bookData.isbn || bookData.authors);

      // Normalize fields: sometimes authors/publisher come as strings or arrays
      const normalizeArray = (v: any): string[] | undefined => {
        if (!v) return undefined;
        if (Array.isArray(v)) return v.filter(Boolean).map(String);
        if (typeof v === 'string') return [v];
        return undefined;
      };

      const authorsArr = normalizeArray(bookData.authors);
      const genresArr = normalizeArray(bookData.genres) || normalizeArray(bookData.categories);
      const publisherStr = Array.isArray(bookData.publisher) ? String(bookData.publisher[0]) : (bookData.publisher || bookData.publishers?.[0]);

      // Check if this is API-fetched book data (has structured fields)
      if (fromAPI) {
        // This is API-fetched book data - use it directly
        if (bookData.title) setValue('title', bookData.title);
        if (bookData.subtitle) setValue('subtitle', bookData.subtitle || '');
        if (bookData.isbn) setValue('isbn', bookData.isbn);

        // Handle publication date - convert various formats
        if (bookData.publication_date) {
          let formattedDate = bookData.publication_date;
          // If it's just a year, add January 1st
          if (/^\d{4}$/.test(formattedDate)) {
            formattedDate = `${formattedDate}-01-01`;
          }
          // If it's YYYY-MM format, add day
          else if (/^\d{4}-\d{2}$/.test(formattedDate)) {
            formattedDate = `${formattedDate}-01`;
          }
          setValue('publication_date', formattedDate);
        }

        if (bookData.description) setValue('description', bookData.description);
        if (bookData.language) setValue('language', bookData.language || 'English');
        if (bookData.pages) setValue('pages', bookData.pages);

        // Handle authors - convert array to form format
        if (authorsArr && authorsArr.length > 0) {
          const matchedAuthorIds: number[] = [];
          const unmatched: string[] = [];
          authorsArr.forEach((authorName) => {
            const matchedAuthor = authors.find(a => a.full_name.toLowerCase() === authorName.toLowerCase());
            if (matchedAuthor) matchedAuthorIds.push(matchedAuthor.id);
            else unmatched.push(authorName);
          });
          if (matchedAuthorIds.length > 0) setValue('author_ids', matchedAuthorIds);
          if (unmatched.length > 0) {
            toast({ title: 'Authors Not Found', description: `Authors not in DB: ${unmatched.join(', ')}. Add them manually if needed.`, variant: 'destructive' });
          }
        }

        // Handle genres - convert array to form format
        if (genresArr && genresArr.length > 0) {
          const matchedGenreIds: number[] = [];
          genresArr.forEach((genreName: string) => {
            const matchedGenre = genres.find(g => g.name.toLowerCase() === genreName.toLowerCase());
            if (matchedGenre) matchedGenreIds.push(matchedGenre.id);
          });
          if (matchedGenreIds.length > 0) {
            setValue('genre_ids', matchedGenreIds);
          }
        }

        // Handle publisher - try to match by name
        if (publisherStr) {
          const matchedPublisher = publishers.find(p => p.name.toLowerCase() === String(publisherStr).toLowerCase());
          if (matchedPublisher) setValue('publisher_id', matchedPublisher.id);
          else {
            toast({ title: 'Publisher Not Found', description: `Publisher "${publisherStr}" was not found in the database. You may need to add it manually.`, variant: 'destructive' });
          }
        }

        toast({
          title: 'Book Info Retrieved',
          description: `Successfully fetched book information for "${bookData.title}"`,
        });

        setActiveTab('catalog');
      } else {
        // This is raw QR data, handle as before
        if (bookData.title) setValue('title', bookData.title);
        if (bookData.subtitle) setValue('subtitle', bookData.subtitle || '');
        if (bookData.isbn) setValue('isbn', bookData.isbn);
        if (bookData.publication_date) setValue('publication_date', bookData.publication_date);
        if (bookData.description) setValue('description', bookData.description || '');
        if (bookData.language) setValue('language', bookData.language || 'English');
        if (bookData.pages) setValue('pages', bookData.pages);

        // Handle authors - try to match by name or create if needed
        if (bookData.authors && Array.isArray(bookData.authors)) {
          const matchedAuthorIds: number[] = [];
          const unmatchedAuthors: string[] = [];

          bookData.authors.forEach((authorName: string) => {
            const matchedAuthor = authors.find(a =>
              a.full_name.toLowerCase() === authorName.toLowerCase()
            );
            if (matchedAuthor) {
              matchedAuthorIds.push(matchedAuthor.id);
            } else {
              unmatchedAuthors.push(authorName);
            }
          });

          if (matchedAuthorIds.length > 0) {
            setValue('author_ids', matchedAuthorIds);
          }

          if (unmatchedAuthors.length > 0) {
            toast({
              title: 'Authors Not Found',
              description: `The following authors were not found in the database: ${unmatchedAuthors.join(', ')}. You may need to add them manually.`,
              variant: 'destructive',
            });
          }
        }

        // Handle genres - try to match by name
        if (bookData.genres && Array.isArray(bookData.genres)) {
          const matchedGenreIds: number[] = [];
          bookData.genres.forEach((genreName: string) => {
            const matchedGenre = genres.find(g =>
              g.name.toLowerCase() === genreName.toLowerCase()
            );
            if (matchedGenre) {
              matchedGenreIds.push(matchedGenre.id);
            }
          });
          if (matchedGenreIds.length > 0) {
            setValue('genre_ids', matchedGenreIds);
          }
        }

        // Handle publisher - try to match by name
        if (bookData.publisher) {
          const matchedPublisher = publishers.find(p =>
            p.name.toLowerCase() === bookData.publisher.toLowerCase()
          );
          if (matchedPublisher) {
            setValue('publisher_id', matchedPublisher.id);
          }
        }

        toast({
          title: 'QR Code Scanned',
          description: 'Book information has been filled from QR code',
        });

        setActiveTab('catalog');
      }
    } catch (error) {
      // If not JSON, try to extract ISBN from plain text
      const isbnMatch = qrData.match(/(\d{10}|\d{13})/);
      if (isbnMatch) {
        setValue('isbn', isbnMatch[1]);
        toast({
          title: 'ISBN Detected',
          description: 'ISBN has been extracted from QR code',
        });
        setActiveTab('catalog');
      } else {
        toast({
          title: 'Invalid QR Code',
          description: 'Could not parse book information from QR code',
          variant: 'destructive',
        });
      }
    }
  };

  const handleQRScanError = (error: string) => {
    toast({
      title: 'Scanner Error',
      description: error,
      variant: 'destructive',
    });
  };

  const onSubmit = async (data: CreateBookWithInventoryFormData) => {
    try {
      // Step 1: Create the book in catalog
      const bookData: CreateBookData = {
        title: data.title,
        subtitle: data.subtitle,
        isbn: data.isbn,
        publication_date: data.publication_date,
        description: data.description,
        language: data.language,
        pages: data.pages,
        edition: data.edition,
        series: data.series,
        series_number: data.series_number,
        author_ids: data.author_ids,
        genre_ids: data.genre_ids || [],
        publisher_id: data.publisher_id,
      };

      let createdBook;

      // Handle cover image upload if provided
      if (data.cover_image) {
        const formDataWithFile = new FormData();

        // Add all book data to FormData
        Object.entries(bookData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(item => formDataWithFile.append(key, item.toString()));
            } else {
              formDataWithFile.append(key, value.toString());
            }
          }
        });

        // Add the cover image file
        formDataWithFile.append('cover_image', data.cover_image);

        // Use a custom API call for file upload
        const response = await fetch('/api/v1/catalog/books/', {
          method: 'POST',
          credentials: 'include',
          body: formDataWithFile,
          headers: {
            'X-Use-Cookie-Auth': 'true',
          }
        });

        if (!response.ok) {
          throw new Error('Failed to create book with cover image');
        }

        createdBook = await response.json();
      } else {
        // Regular book creation without file
        createdBook = await createBookMutation.mutateAsync(bookData);
      }

      // Step 2: Create book copies if requested
      if (data.create_copies && data.copies && data.copies.length > 0) {
        const copyCreationPromises = data.copies.map(async (copyData) => {
          const bookCopyData: CreateBookCopyData = {
            book_id: createdBook.id,
            barcode: copyData.barcode,
            accession_number: copyData.accession_number,
            acquisition_date: copyData.acquisition_date,
            acquisition_cost: copyData.acquisition_cost,
            location_id: copyData.location_id,
            condition_id: copyData.condition_id,
            edition_notes: copyData.notes,
          };

          return createBookCopyMutation.mutateAsync(bookCopyData);
        });

        await Promise.all(copyCreationPromises);
      }

      toast({
        title: 'Success',
        description: `Book "${data.title}" created successfully${data.create_copies ? ` with ${data.copies?.length} copies` : ''}`,
      });

      onSuccess();
      onClose();
      form.reset(buildDefaultFormValues());
    } catch (error) {
      console.error('Error creating book:', error);
      toast({
        title: 'Error',
        description: 'Failed to create book. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const isPending = createBookMutation.isPending || createBookCopyMutation.isPending;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title="Add New Book"
        width={560}
        footer={
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isPending}
              className="bg-[var(--lib-gold)] hover:brightness-105 disabled:opacity-50 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Book {createCopies && copies.length > 0 && `& ${copies.length} Copies`}
            </button>
          </div>
        }
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="catalog">Catalog Information</TabsTrigger>
                <TabsTrigger value="inventory">Inventory Copies</TabsTrigger>
              </TabsList>

              <TabsContent value="catalog" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[var(--lib-ink)]">Book Details</h3>
                  <button
                    type="button"
                    onClick={() => setIsQRScannerOpen(true)}
                    className="flex items-center gap-1.5 text-xs text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] border border-[var(--lib-hairline)] rounded-lg px-2.5 py-1.5 hover:bg-[var(--lib-surface-2)] transition-colors"
                  >
                    <QrCode className="h-3.5 w-3.5" />
                    Scan QR
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Title *</FormLabel>
                        <FormControl>
                          <Input placeholder="Book title" {...field} className={inputCls} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subtitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Subtitle</FormLabel>
                        <FormControl>
                          <Input placeholder="Book subtitle" {...field} className={inputCls} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="isbn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>ISBN *</FormLabel>
                          <FormControl>
                            <Input placeholder="ISBN-13 or ISBN-10" {...field} className={inputCls} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="publication_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Publication Date *</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} className={inputCls} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="author_ids"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Authors *</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const currentAuthors = field.value || [];
                            const authorId = parseInt(value);
                            if (!currentAuthors.includes(authorId)) {
                              field.onChange([...currentAuthors, authorId]);
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger className={inputCls}>
                              <SelectValue placeholder="Select authors" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {authors.map((author) => (
                              <SelectItem key={author.id} value={author.id.toString()}>
                                {author.id}: {author.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {field.value && field.value.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {field.value.map((authorId) => {
                              const author = authors.find(a => a.id === authorId);
                              return (
                                <span
                                  key={authorId}
                                  className="bg-[var(--lib-surface-2)] text-[var(--lib-ink)] border border-[var(--lib-hairline)] px-2 py-0.5 rounded text-xs flex items-center gap-1"
                                >
                                  {author?.full_name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      field.onChange(field.value.filter(id => id !== authorId));
                                    }}
                                    className="ml-1 text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)]"
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="publisher_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Publisher *</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))}>
                          <FormControl>
                            <SelectTrigger className={inputCls}>
                              <SelectValue placeholder="Select publisher" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {publishers.map((publisher) => (
                              <SelectItem key={publisher.id} value={publisher.id.toString()}>
                                {publisher.id}: {publisher.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="genre_ids"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Genres</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const currentGenres = field.value || [];
                            const genreId = parseInt(value);
                            if (!currentGenres.includes(genreId)) {
                              field.onChange([...currentGenres, genreId]);
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger className={inputCls}>
                              <SelectValue placeholder="Select genres" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {genres.map((genre) => (
                              <SelectItem key={genre.id} value={genre.id.toString()}>
                                {genre.id}: {genre.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {field.value && field.value.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {field.value.map((genreId) => {
                              const genre = genres.find(g => g.id === genreId);
                              return (
                                <span
                                  key={genreId}
                                  className="bg-green-50 text-green-800 border border-green-200 px-2 py-0.5 rounded text-xs flex items-center gap-1"
                                >
                                  {genre?.name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      field.onChange(field.value!.filter(id => id !== genreId));
                                    }}
                                    className="ml-1 text-green-500 hover:text-green-700"
                                  >
                                    ×
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="language"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Language</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className={inputCls}>
                                <SelectValue placeholder="Select language" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="English">English</SelectItem>
                              <SelectItem value="Spanish">Spanish</SelectItem>
                              <SelectItem value="French">French</SelectItem>
                              <SelectItem value="German">German</SelectItem>
                              <SelectItem value="Chinese">Chinese</SelectItem>
                              <SelectItem value="Japanese">Japanese</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pages"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Pages</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="Number of pages"
                              {...field}
                              onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                              className={inputCls}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="edition"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Edition</FormLabel>
                          <FormControl>
                            <Input placeholder="1st, 2nd, Revised, etc." {...field} className={inputCls} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="series"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={labelCls}>Series</FormLabel>
                          <FormControl>
                            <Input placeholder="Book series name" {...field} className={inputCls} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Cover Image Field */}
                  <FormField
                    control={form.control}
                    name="cover_image"
                    render={({ field: { value, onChange, ...field } }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Cover Image</FormLabel>
                        <FormControl>
                          <Input
                            type="file"
                            accept="image/*"
                            {...field}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              onChange(file);
                            }}
                            className={inputCls}
                          />
                        </FormControl>
                        <p className="text-xs text-[var(--lib-ink-muted)] mt-1">Upload a cover image (optional)</p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className={labelCls}>Description</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Book description..." {...field} className={inputCls} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent value="inventory" className="space-y-4 mt-4">
                <div className="flex items-center space-x-2">
                  <FormField
                    control={form.control}
                    name="create_copies"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-medium text-[var(--lib-ink)]">Create physical copies</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                {createCopies && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-[var(--lib-ink)]">Book Copies</h3>
                      <button
                        type="button"
                        onClick={addCopy}
                        className="flex items-center gap-1.5 text-xs text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] border border-[var(--lib-hairline)] rounded-lg px-2.5 py-1.5 hover:bg-[var(--lib-surface-2)] transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Copy
                      </button>
                    </div>

                    {copies.map((copy, index) => (
                      <div key={index} className="border border-[var(--lib-hairline)] p-4 rounded-lg space-y-4 bg-[var(--lib-surface-2)]">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-[var(--lib-ink)]">Copy #{index + 1}</h4>
                          {copies.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCopy(index)}
                              className="p-1 rounded text-[var(--lib-ink-muted)] hover:text-[var(--lib-danger)] hover:bg-[var(--lib-surface-2)] transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                          <FormField
                            control={form.control}
                            name={`copies.${index}.barcode`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className={labelCls}>Barcode *</FormLabel>
                                <FormControl>
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="12-digit internal barcode"
                                      inputMode="numeric"
                                      maxLength={12}
                                      {...field}
                                      onChange={(event) => field.onChange(sanitizeCopyBarcode(event.target.value))}
                                      className={inputCls}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => autoFillCopyData(index)}
                                      className="px-2.5 py-1.5 text-xs border border-[var(--lib-hairline)] rounded-lg text-[var(--lib-ink-muted)] hover:bg-[var(--lib-surface-2)] transition-colors whitespace-nowrap"
                                    >
                                      Auto
                                    </button>
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`copies.${index}.accession_number`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className={labelCls}>Accession Number *</FormLabel>
                                <FormControl>
                                  <Input placeholder="2024.01.001" {...field} className={inputCls} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <div className="grid grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name={`copies.${index}.acquisition_date`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className={labelCls}>Acquisition Date *</FormLabel>
                                  <FormControl>
                                    <Input type="date" {...field} className={inputCls} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`copies.${index}.acquisition_cost`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className={labelCls}>Cost</FormLabel>
                                  <FormControl>
                                    <Input placeholder="29.99" {...field} className={inputCls} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name={`copies.${index}.location_id`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className={labelCls}>Location</FormLabel>
                                <Select
                                  onValueChange={(value) => {
                                    field.onChange(value === "none" ? undefined : parseInt(value));
                                  }}
                                  value={field.value ? field.value.toString() : "none"}
                                >
                                  <FormControl>
                                    <SelectTrigger className={inputCls}>
                                      <SelectValue placeholder="Select location" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="none">No location assigned</SelectItem>
                                    {locations.map((location) => (
                                      <SelectItem key={location.id} value={location.id.toString()}>
                                        {location.label} · {location.library}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`copies.${index}.condition_id`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className={labelCls}>Condition *</FormLabel>
                                <Select
                                  onValueChange={(value) => field.onChange(parseInt(value))}
                                  value={field.value?.toString()}
                                >
                                  <FormControl>
                                    <SelectTrigger className={inputCls}>
                                      <SelectValue placeholder="Select condition" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {conditions.map((condition) => (
                                      <SelectItem key={condition.id} value={condition.id.toString()}>
                                        {condition.name} (Score: {condition.score})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </Drawer>

      {/* QR Scanner Dialog */}
      <QRScanner
        isOpen={isQRScannerOpen}
        onClose={() => setIsQRScannerOpen(false)}
        onScan={handleQRScan}
        onError={handleQRScanError}
      />
    </>
  );
};

export default AddBookWithInventoryForm;
